'use client';

import { type ReactNode, useMemo, useState } from 'react';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { useCurrentUserProfile } from '@ps/ui/contexts/user-profile';
import { BehaviorSubject } from 'rxjs';

import { useJoinChatRoom } from '../../hooks/useJoinChatRoom';
import type { ChatMessage } from '../../types/ChatMessage';
import type { ChatSessionType } from '../../types/ChatSessionType';
import type { ChatUser } from '../../types/ChatUser';
import { ChatMessageProvider } from '../chat-messages/provider';
import { ChatContext, type ChatContextValue } from './context';

interface Props {
  sessionType: ChatSessionType;
  roomId: string;
  sessionId: string;
  currentUserId: string;
  /** Best-effort display name + avatar for the current user; falls
      back to the auth profile when omitted. */
  targetUserId: string;
  targetUser: Pick<ChatUser, 'name' | 'avatar'>;
  transient?: boolean;
  backButton?: boolean;
  /** The chat's bio context, or `null` on the non-1:1 DM inbox. */
  bioContext: BioContext | null;
  messageToReply?: ChatMessage;
  children?: ReactNode;
}

/**
 * Per-room provider. Mounts the join lifecycle, builds the
 * `currentUser` / `targetUser` ChatUser objects, and wraps
 * `ChatMessageProvider` so consumers get both contexts with one
 * mount.
 *
 * Current-user metadata is sourced from `UserProfileContext`; the
 * target-user metadata comes in via props (the sessions list already
 * has it from `chatControllerGetSessions`).
 */
export function ChatProvider({
  sessionType,
  roomId,
  sessionId,
  currentUserId,
  targetUserId,
  targetUser: targetUserOverride,
  transient,
  backButton,
  bioContext,
  messageToReply,
  children,
}: Props) {
  const currentUserProfile = useCurrentUserProfile();
  const [quotedMessage$] = useState(
    () => new BehaviorSubject<ChatMessage | undefined>(messageToReply),
  );

  const { state: joinState } = useJoinChatRoom({
    sessionType,
    roomId,
    transient,
    currentUserId,
    targetUserId,
  });

  const currentUser = useMemo<ChatUser>(() => {
    const profile = currentUserProfile;
    const first = profile?.firstName?.trim() ?? '';
    const last = profile?.lastName?.trim() ?? '';
    const name =
      profile?.username?.trim() || `${first} ${last}`.trim() || 'You';
    return {
      userId: currentUserId,
      name,
      avatar: profile?.avatar ?? '',
    };
  }, [currentUserId, currentUserProfile]);

  const targetUser = useMemo<ChatUser>(
    () => ({
      userId: targetUserId,
      name: targetUserOverride.name || 'Unknown',
      avatar: targetUserOverride.avatar ?? '',
    }),
    [targetUserId, targetUserOverride.name, targetUserOverride.avatar],
  );

  const value = useMemo<ChatContextValue>(
    () => ({
      sessionType,
      roomId,
      sessionId,
      currentUserId,
      targetUserId,
      currentUser,
      targetUser,
      users: [currentUser, targetUser],
      transient,
      backButton,
      bioContext,
      quotedMessage$,
      joinState,
    }),
    [
      sessionType,
      roomId,
      sessionId,
      currentUserId,
      targetUserId,
      currentUser,
      targetUser,
      transient,
      backButton,
      bioContext,
      quotedMessage$,
      joinState,
    ],
  );

  return (
    <ChatContext.Provider value={value}>
      <ChatMessageProvider>{children}</ChatMessageProvider>
    </ChatContext.Provider>
  );
}
